import type { FormDefinition } from './types';

const COMPACT_EXAMPLE: FormDefinition = {
  fields: [
    { type: 'Information', label: 'Site inspection', isRequired: false },
    { type: 'Date', label: 'Inspection date', isRequired: true },
    { type: 'Text', label: 'Inspector name', isRequired: true },
    {
      type: 'RadioButtons',
      label: 'Overall condition',
      options: ['Good', 'Fair', 'Poor'],
      isRequired: false,
    },
    {
      type: 'Checkboxes',
      label: 'Items requiring attention',
      options: ['Roof', 'Drainage', 'Lighting'],
      isRequired: false,
    },
    { type: 'Notes', label: 'Details', isRequired: false },
    { type: 'Signature', label: 'Inspector signature', isRequired: true },
  ],
};

const GUIDELINES = `You are a form generation assistant. Your task is to create a JSON form definition based on user input.

## Field Types and Usage Guidelines

**Information**: Use for section headers, labels, or informational text that groups related fields together. Always set isRequired: false.

**Text**: Single-line text input fields.

**Notes**: Multi-line text area for longer text input.

**Date**: Date picker field.

**Time**: Time picker field.

**YesNo**: Boolean checkbox for a single yes/no question. Do NOT use YesNo for inspection checklist rows—use Checkboxes.

**Checkboxes**: Multiple selection; requires "options". For inspection checklists, use ONE Checkboxes field with all items as options.

**Number**: Numeric input.

**RadioButtons**: Single selection; requires "options". Preferred for single-select.

**Dropdown**: Single-select; requires "options". Use only when many options make RadioButtons impractical.

**Signature** / **SignatureImage**: Signature capture.

**Images**: Image upload field.

**VehicleLookup** / **TrailerLookup** / **EquipmentLookup**: Database lookups. EquipmentLookup may include "filter" as JSON string.

## Detection rules

Prefer RadioButtons over Dropdown for single-select. Inspection item lists → single Checkboxes field. "Vehicle"/"trailer"/"equipment" in text → matching Lookup types.

## Output

Return ONLY valid JSON:
{"fields":[{"type":"FieldType","label":"...","isRequired":true|false,"options":["..."]}]}
Include "options" only for Dropdown, RadioButtons, Checkboxes.

## Example

\`\`\`json
${JSON.stringify(COMPACT_EXAMPLE, null, 2)}
\`\`\`

Follow the user input and spreadsheet context below. Return ONLY the JSON object, no markdown fences or explanation.`;

export function buildFormGenerationPrompt(
  userInput: string,
  spreadsheetData?: string,
): string {
  const inputSection =
    spreadsheetData !== undefined && spreadsheetData !== ''
      ? `## Spreadsheet data\n\`\`\`\n${spreadsheetData}\n\`\`\`\n\n## Description\n${userInput}`
      : `## User input\n${userInput}`;

  return `${GUIDELINES}\n\n${inputSection}\n\nNow generate the form definition JSON:`;
}
